import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Printer, X, Loader2, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Anteprima di un documento prima di salvarlo o stamparlo.
 *
 * Prima ogni esportazione partiva dritta in download: per guardare un report
 * bisognava per forza salvarlo su disco. Qui il documento si apre in una
 * finestra di consultazione, e scaricare o stampare restano due scelte.
 *
 * - PDF: mostrato nel visualizzatore del browser.
 * - CSV / Excel: mostrato come tabella leggibile.
 */
export default function DocumentPreview({
  open,
  onOpenChange,
  url,
  token,
  titolo = 'Anteprima documento',
  descrizione,
  nomeFile = 'documento',
  formato = 'pdf',
}) {
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState(null);
  const [urlOggetto, setUrlOggetto] = useState(null);
  const [righe, setRighe] = useState(null);
  const blobRef = useRef(null);

  useEffect(() => {
    if (!open || !url) return undefined;
    let annullato = false;
    const controller = new AbortController();

    (async () => {
      setCaricamento(true);
      setErrore(null);
      setRighe(null);
      try {
        // documento gia' in memoria (blob creato da chi ci chiama): niente richiesta
        if (url.startsWith('blob:')) {
          if (formato === 'pdf') {
            setUrlOggetto(url);
          } else {
            const b = await (await fetch(url)).blob();
            blobRef.current = b;
            const testoBlob = await b.text();
            setRighe(
              testoBlob
                .split(/\r?\n/)
                .filter((r) => r.trim() !== '')
                .slice(0, 500)
                .map((r) => r.split(testoBlob.includes(';') ? ';' : ','))
            );
          }
          setCaricamento(false);
          return;
        }
        // il parametro chiede al server di rispondere "inline" invece che come allegato
        const separatore = url.includes('?') ? '&' : '?';
        const risposta = await fetch(`${url}${separatore}inline=1`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!risposta.ok) {
          const testo = await risposta.text().catch(() => '');
          throw new Error(testo.slice(0, 200) || `Errore ${risposta.status}`);
        }
        const blob = await risposta.blob();
        if (annullato) return;
        blobRef.current = blob;

        if (formato === 'pdf') {
          setUrlOggetto(URL.createObjectURL(blob));
        } else {
          const testo = await blob.text();
          const separa = testo.includes(';') && !testo.includes(',')
            ? ';'
            : (testo.split('\n')[0] || '').split(';').length >
              (testo.split('\n')[0] || '').split(',').length
              ? ';' : ',';
          setRighe(
            testo
              .split(/\r?\n/)
              .filter((r) => r.trim() !== '')
              .slice(0, 500)
              .map((r) => r.split(separa))
          );
        }
      } catch (e) {
        if (!annullato && e.name !== 'AbortError') setErrore(e.message);
      } finally {
        if (!annullato) setCaricamento(false);
      }
    })();

    return () => {
      annullato = true;
      controller.abort();
    };
  }, [open, url, token, formato]);

  // libera la memoria quando la finestra si chiude
  useEffect(() => {
    if (open) return undefined;
    return () => {
      if (urlOggetto && urlOggetto !== url) URL.revokeObjectURL(urlOggetto);
    };
  }, [open, urlOggetto, url]);

  const scarica = () => {
    if (!blobRef.current) {
      if (urlOggetto) {
        const a0 = document.createElement('a');
        a0.href = urlOggetto;
        a0.download = nomeFile;
        document.body.appendChild(a0);
        a0.click();
        document.body.removeChild(a0);
        toast.success('Documento salvato');
      }
      return;
    }
    const u = URL.createObjectURL(blobRef.current);
    const a = document.createElement('a');
    a.href = u;
    a.download = nomeFile;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(u);
    toast.success('Documento salvato');
  };

  const stampa = () => {
    if (formato !== 'pdf' || !urlOggetto) {
      window.print();
      return;
    }
    const finestra = window.open(urlOggetto, '_blank');
    if (!finestra) {
      toast.error('Il browser ha bloccato la finestra di stampa');
      return;
    }
    finestra.addEventListener('load', () => finestra.print(), { once: true });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[88vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle>{titolo}</DialogTitle>
          <DialogDescription>
            {descrizione
              || 'Consulta il documento. Salvarlo o stamparlo resta una tua scelta.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-stone-100">
          {caricamento && (
            <div className="h-full flex items-center justify-center text-stone-600">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Preparazione dell&apos;anteprima…
            </div>
          )}

          {!caricamento && errore && (
            <div className="h-full flex items-center justify-center p-8">
              <div className="max-w-md text-center">
                <p className="font-medium text-red-700">
                  Non è stato possibile generare l&apos;anteprima
                </p>
                <p className="text-sm text-stone-600 mt-2">{errore}</p>
              </div>
            </div>
          )}

          {!caricamento && !errore && formato === 'pdf' && urlOggetto && (
            <iframe
              title={titolo}
              src={`${urlOggetto}#toolbar=1&navpanes=0`}
              className="w-full h-full border-0"
            />
          )}

          {!caricamento && !errore && formato !== 'pdf' && righe && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3 text-sm text-stone-600">
                <FileSpreadsheet className="h-4 w-4" />
                {righe.length - 1} righe di dati
                {righe.length >= 500 && ' (mostrate le prime 500)'}
              </div>
              <div className="overflow-auto bg-white rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      {(righe[0] || []).map((c, i) => (
                        <th key={i} className="text-left px-3 py-2 font-medium border-b whitespace-nowrap">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {righe.slice(1).map((r, i) => (
                      <tr key={i} className={i % 2 ? 'bg-stone-50/60' : ''}>
                        {r.map((c, j) => (
                          <td key={j} className="px-3 py-1.5 border-b whitespace-nowrap">{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-between items-center gap-2">
          <p className="text-xs text-stone-500">
            Nessun file è stato salvato sul tuo computer.
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4 mr-2" />
              Chiudi
            </Button>
            <Button variant="outline" onClick={stampa} disabled={caricamento || !!errore}>
              <Printer className="h-4 w-4 mr-2" />
              Stampa
            </Button>
            <Button onClick={scarica} disabled={caricamento || !!errore}>
              <Download className="h-4 w-4 mr-2" />
              Salva
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
